import { useResolvedUser } from '@tcc/shared-data-lib';
import { useMemo } from 'react';
import { useUserLoggedInConfigs } from '../utils/sharedData';
import { isDealerImpersonatingFarmer, isDealerImpersonatingUser, isEmployeeImpersonatingUser, isImpersonating, isImpersonationNotAvailable, isProxyingItself } from '../utils/isImpersonating';
import { ImpersonationMode } from '../types/impersonationModes';
import { User } from '../types/user';

export type UseImpersonationMode = {
  isLoading: boolean;
  impersonationModes: ImpersonationMode[];
  user?: User;
  impersonatedUser?: User;
  isImpersonating: boolean;
};

export const useImpersonationMode = (): UseImpersonationMode => {
  const {
    loggedInUser,
    impersonatedUser,
    isLoading: isResolvedUserLoading,
  } = useResolvedUser(useUserLoggedInConfigs);

  const impersonationModes = useMemo(() => {
    const impersonationModesSet: Set<ImpersonationMode> = new Set();
    if (!isResolvedUserLoading && loggedInUser) {
      if (isImpersonationNotAvailable(loggedInUser.details)) impersonationModesSet.add(ImpersonationMode.IMPERSONATION_NOT_AVAILABLE);
      if (isImpersonating(loggedInUser.details)) impersonationModesSet.add(ImpersonationMode.IMPERSONATION_ACTIVE);
      if (isProxyingItself(loggedInUser.details)) impersonationModesSet.add(ImpersonationMode.PROXYING_ITSELF).add(ImpersonationMode.IMPERSONATION_ACTIVE);
      if (isDealerImpersonatingFarmer(loggedInUser, impersonatedUser)) impersonationModesSet.add(ImpersonationMode.DEALER_PROXYING_FARMER);
      if (isDealerImpersonatingUser(loggedInUser, impersonatedUser)) impersonationModesSet.add(ImpersonationMode.DEALER_PROXYING_USER);
      if (isEmployeeImpersonatingUser(loggedInUser, impersonatedUser)) impersonationModesSet.add(ImpersonationMode.EMPLOYEE_PROXYING_USER);
    }

    return Array.from(impersonationModesSet);
  }, [
    isResolvedUserLoading,
    loggedInUser,
    impersonatedUser
  ]);

  const inpersonationActive = impersonationModes.includes(ImpersonationMode.IMPERSONATION_ACTIVE) &&
    !impersonationModes.includes(ImpersonationMode.PROXYING_ITSELF);

  return {
    isLoading: isResolvedUserLoading,
    impersonationModes,
    user: loggedInUser,
    impersonatedUser: impersonatedUser || loggedInUser,
    isImpersonating: inpersonationActive
  };
};
